import React, { useState, useEffect, useMemo, useCallback, createContext, useContext } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import Dropdown from 'react-bootstrap/Dropdown';
import config from "../config";
import api from "../lib/request.js";
import HeaderNavbar from '../components/HeaderNavbar';
import LazyImage from '../components/LazyImage';
import EditableTextArea from '../components/EditableTextArea';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRightArrowLeft,
  faGear,
  faPenToSquare,
  faTrash,
  faTriangleExclamation,
  faCircle,
  faCircleInfo,
  faLightbulb
} from '@fortawesome/free-solid-svg-icons'
import { useLocation } from 'react-router-dom';
import Page404 from './404';
import { useAuthContext } from '../contexts/AuthContext';
import Footer from '../components/Footer';
import moment from 'moment';
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort'
import { arrayMoveImmutable } from "array-move";
import { bomReferenceRemarkPlugin, parseMarkdownReferenceHref } from '../lib/bomMarkdown';
import { sanitizeMarkdownPreviewText } from '../lib/markdownPreview';
import './Project.css';

function parseFilterByTag(search) {
  const params = new URLSearchParams(search);
  const values = params.getAll('filterByTag');
  const seen = new Set();
  const tags = [];

  values.forEach((rawValue) => {
    (rawValue || '').toString().split(',').forEach((rawTag) => {
      const tag = rawTag.trim();
      if (!tag) return;
      const key = tag.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      tags.push(tag);
    });
  });

  return tags;
}

function normalizeTagList(values) {
  const seen = new Set();
  const tags = [];
  const tagsArray = Array.isArray(values) ? values : [];

  tagsArray.forEach((value) => {
    const tag = (value || '').toString().trim();
    if (!tag) return;
    const key = tag.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    tags.push(tag);
  });

  return tags.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

function normalizeGuideTags(guides) {
  const parsedTags = [];
  if (Array.isArray(guides)) {
    guides.forEach((guide) => {
      const guideTags = Array.isArray(guide?.tags) ? guide.tags : [];
      parsedTags.push(...guideTags);
    });
  }
  return normalizeTagList(parsedTags);
}

function normalizeBomReferenceKey(value) {
  return (value || '').toString().trim().toLowerCase();
}

const BOM_URL_VIEW_PARAM = 'bomView';
const BOM_URL_FOCUS_PARAM = 'bomFocus';
const BOM_URL_VERSION_PARAM = 'bomVersion';
const BOM_URL_ITEM_PARAM = 'bomItem';
const BOM_FILTER_TYPES = new Set(['category', 'module', 'version']);
const ListTypeContext = createContext('ul');

function normalizeBomFilterType(value) {
  const normalized = (value || '').toString().trim().toLowerCase();
  return BOM_FILTER_TYPES.has(normalized) ? normalized : '';
}

function toClientBomReferenceItems(rawItems) {
  const items = Array.isArray(rawItems) ? rawItems : [];
  return items
    .map((item) => {
      const identifier = (item?.identifier || '').toString().trim().toLowerCase();
      if (!identifier) return null;

      const title = (item?.name || item?.title || '').toString().trim();
      const description = (item?.description || item?.content || '').toString();
      const category = (item?.category || '').toString().trim();
      const module = (item?.module || '').toString().trim();
      const versions = Array.isArray(item?.versions)
        ? item.versions.map((version) => (version || '').toString().trim()).filter(Boolean)
        : [];

      let imageUrl = '';
      if (item?.image?.url) {
        imageUrl = item.image.url;
      } else if (Array.isArray(item?.images) && item.images[0]?.url) {
        imageUrl = item.images[0].url;
      }

      return {
        stepId: (item?.stepId || '').toString().trim(),
        position: Number(item?.position) || null,
        identifier,
        name: title || identifier,
        description,
        category,
        module,
        versions,
        imageUrl
      };
    })
    .filter(Boolean);
}

function toClientBomReferences(rawGuides) {
  const guides = Array.isArray(rawGuides) ? rawGuides : [];
  return guides
    .map((guide) => {
      const type = (guide?.type || '').toString().trim().toLowerCase();
      const slugURL = (guide?.slugURL || '').toString().trim();
      if (type !== 'bom' || !slugURL) return null;
      return {
        id: guide?.id,
        slugURL,
        title: (guide?.title || slugURL).toString().trim() || slugURL,
        items: toClientBomReferenceItems(guide?.steps)
      };
    })
    .filter(Boolean);
}

function buildBomReferenceLookup(catalog) {
  const boms = Array.isArray(catalog) ? catalog : [];
  const bomMap = new Map();

  boms.forEach((bom) => {
    const bomKey = normalizeBomReferenceKey(bom?.slugURL);
    if (!bomKey) return;
    const itemMap = new Map();
    const items = Array.isArray(bom?.items) ? bom.items : [];
    items.forEach((item) => {
      const itemKey = normalizeBomReferenceKey(item?.identifier);
      if (!itemKey || itemMap.has(itemKey)) return;
      itemMap.set(itemKey, item);
    });
    bomMap.set(bomKey, {
      ...bom,
      itemMap
    });
  });

  return bomMap;
}

function toClientGuideReferences(rawGuides) {
  const guides = Array.isArray(rawGuides) ? rawGuides : [];
  return guides
    .map((entry) => {
      const slugURL = (entry?.slugURL || '').toString().trim();
      const type = (entry?.type || 'guide').toString().trim().toLowerCase();
      if (!slugURL || type === 'bom') return null;
      return {
        id: entry?.id,
        slugURL,
        title: (entry?.title || slugURL).toString().trim() || slugURL
      };
    })
    .filter(Boolean);
}

function buildGuideReferenceLookup(catalog) {
  const guides = Array.isArray(catalog) ? catalog : [];
  const guideMap = new Map();
  guides.forEach((entry) => {
    const key = normalizeBomReferenceKey(entry?.slugURL);
    if (!key || guideMap.has(key)) return;
    guideMap.set(key, entry);
  });
  return guideMap;
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getFirstTextValue(node) {
  if (!node) return '';
  if (node.type === 'text' && typeof node.value === 'string') return node.value;
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      const textValue = getFirstTextValue(child);
      if (textValue && textValue.trim()) return textValue;
    }
  }
  return '';
}

function stripLeadingFromReactChildren(children, pattern) {
  if (!pattern) return children;
  let stripped = false;

  const stripChild = (child) => {
    if (stripped) return child;
    if (typeof child === 'string') {
      const next = child.replace(pattern, () => {
        stripped = true;
        return '';
      });
      return next;
    }
    if (React.isValidElement(child) && child.props?.children) {
      const nextChildren = React.Children.map(child.props.children, stripChild);
      return React.cloneElement(child, { ...child.props }, nextChildren);
    }
    return child;
  };

  return React.Children.map(children, stripChild);
}

function CustomListItem(props) {
  const listType = useContext(ListTypeContext);
  const insideOL = listType === 'ol';
  const children = props.children;
  let color = 'black';
  let text = children;
  const colorIconMap = {
    blue: faCircle,
    red: faCircle,
    pink: faCircle,
    black: faCircle,
    green: faCircle,
    violet: faCircle,
    orange: faCircle,
    yellow: faCircle,
    lightblue: faCircle,
    alert: faTriangleExclamation,
    info: faCircleInfo,
    hint: faLightbulb
  };

  let hexColor = '';

  const firstText = getFirstTextValue(props.node);
  const match = firstText.trimStart().match(/^(\S+)\s+/);
  const candidateToken = match ? match[1] : null;
  const isHexColor = candidateToken && /^#[0-9A-Fa-f]{6}$/.test(candidateToken);
  const isNamedColor = candidateToken && colorIconMap[candidateToken];

  let stripPattern = null;
  if (candidateToken && (isNamedColor || isHexColor)) {
    color = candidateToken;
    if (isHexColor) {
      hexColor = candidateToken;
    }
    stripPattern = new RegExp(`^\\s*${escapeRegExp(candidateToken)}\\s+`);
  }

  const colorClassMap = {
    alert: 'red',
    info: 'info'
  };

  if (stripPattern) {
    text = stripLeadingFromReactChildren(children, stripPattern);
  }

  return (
    <li>
      {!insideOL && (
        hexColor ? (
          <FontAwesomeIcon style={{ color: hexColor }} className="bullet-dynamic" icon={faCircle} />
        ) : (
          <FontAwesomeIcon className={`bullet-${colorClassMap[color] || color}`} icon={colorIconMap[color]} />
        )
      )}
      <span className={`step-text${insideOL ? '-ol' : ''}${colorClassMap[color] ? `-${colorClassMap[color]}` : ''}`}>{text}</span>
    </li>
  );
}

function ProjectHome() {
  const { isAuthenticated, isSiteAdmin, canAdminOrganization } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  let { project } = useParams();
  const selectedTags = useMemo(() => parseFilterByTag(location.search), [location.search]);
  const hasTagFilters = selectedTags.length > 0;

  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createModalType, setCreateModalType] = useState('');
  const [createModalData, setCreateModalData] = useState({ title: '', description: '' });
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectSaving, setProjectSaving] = useState(false);
  const [projectDeleting, setProjectDeleting] = useState(false);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [projectModalData, setProjectModalData] = useState({
    title: '',
    subTitle: '',
    organizationId: '',
    file: null
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      selectedTags.forEach((tag) => {
        params.append('filterByTag', tag);
      });
      const query = params.toString();
      const endpoint = query
        ? `/api/v1/projects/${project}?${query}`
        : `/api/v1/projects/${project}`;
      const response = await api.get(endpoint);
      if (!Array.isArray(response?.allTags)) {
        response.allTags = normalizeGuideTags(response?.guides || []);
      }
      setData(response);
    } catch (e) {
      if (e.statusCode === 404) {
        return setError('404')
      }
      console.log(e);
    }
  }, [project, selectedTags]);

  const markdownRemarkPlugins = useMemo(() => [bomReferenceRemarkPlugin], []);
  const bomReferenceCatalog = useMemo(() => toClientBomReferences(data?.guides), [data?.guides]);
  const bomReferenceLookup = useMemo(() => buildBomReferenceLookup(bomReferenceCatalog), [bomReferenceCatalog]);
  const guideReferenceCatalog = useMemo(() => toClientGuideReferences(data?.guides), [data?.guides]);
  const guideReferenceLookup = useMemo(() => buildGuideReferenceLookup(guideReferenceCatalog), [guideReferenceCatalog]);

  const fetchOrganizations = useCallback(async () => {
    if (!isAuthenticated) {
      setOrganizations([]);
      return [];
    }
    setLoadingOrganizations(true);
    try {
      const endpoint = isSiteAdmin ? '/api/v1/organizations' : '/api/v1/organizations/mine';
      const response = await api.get(endpoint);
      const orgs = Array.isArray(response?.organizations) ? response.organizations : [];
      setOrganizations(orgs);
      return orgs;
    } catch (e) {
      console.log('Error fetching organizations', e);
      setOrganizations([]);
      return [];
    } finally {
      setLoadingOrganizations(false);
    }
  }, [isAuthenticated, isSiteAdmin]);

  const canManageProject = isAuthenticated && canAdminOrganization(data?.org);
  const projectStatus = (data?.status || '').toString().toLowerCase();
  const isPending = projectStatus === 'pending';
  const canEditProject = canManageProject && !isPending;
  const canDeleteProject = isSiteAdmin;
  const showProjectSettingsMenu = canEditProject || canDeleteProject;

  const organizationOptions = useMemo(() => {
    const currentOrgId = Number(data?.org);
    if (!Number.isFinite(currentOrgId)) {
      return organizations;
    }
    const hasCurrentOrg = organizations.some((org) => Number(org?.id) === currentOrgId);
    if (hasCurrentOrg) {
      return organizations;
    }
    return [
      {
        id: currentOrgId,
        name: `Current organization (ID ${currentOrgId})`,
        slug: '',
        status: 'enabled'
      },
      ...organizations
    ];
  }, [data?.org, organizations]);

  function handleShowCreateModal(type) {
    setCreateModalType(type);
    setCreateModalData({
      title: '',
      description: '',
    });
    setShowCreateModal(true);
  }

  function handleCloseCreateModal() {
    setShowCreateModal(false);
  }

  function handleCreateInputChange(event) {
    setCreateModalData({
      ...createModalData,
      [event.target.name]: event.target.value,
    });
  }

  async function handleCreateSubmit(event) {
    event.preventDefault();
    setCreateLoading(true);

    if (!createModalData?.title.trim()) {
      alert('Title is required')
      setCreateLoading(false);
      return;
    }

    try {
      const payload = {
        ...createModalData,
        type: createModalType === 'new-bom' ? 'bom' : 'guide'
      };
      const response = await api.post(`/api/v1/projects/${data.slugURL}/guides`, payload);
      setCreateLoading(false);
      if (response.ok === true) {
        handleCloseCreateModal();
        document.location = `${config.prefixPath}/projects/${data.slugURL}/guides/${response.createdSlugURL}`
      } else {
        if (response.error) {
          alert(response.message);
        }
      }
    } catch (error) {
      setCreateLoading(false);
    }
  }

  function buildProjectFormData(payload = {}) {
    const formData = new FormData();
    formData.append('title', (payload?.title || '').toString());
    formData.append('subTitle', (payload?.subTitle || '').toString());
    formData.append('description', (payload?.description || '').toString());
    if (payload?.organizationId !== undefined && payload?.organizationId !== null && payload.organizationId !== '') {
      formData.append('organizationId', String(payload.organizationId));
    }
    if (payload?.file) {
      formData.append('images', payload.file);
    }
    return formData;
  }

  const submitProjectUpdate = useCallback(async (payload) => {
    return api({
      method: 'put',
      url: `/api/v1/projects/${data?.slugURL}`,
      data: buildProjectFormData(payload),
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }, [data?.slugURL]);

  function handleProjectInputChange(event) {
    setProjectModalData((current) => ({
      ...current,
      [event.target.name]: event.target.value
    }));
  }

  function handleProjectFileChange(event) {
    const file = event.target.files?.[0] || null;
    setProjectModalData((current) => ({
      ...current,
      file
    }));
  }

  async function openProjectModal() {
    const loadedOrganizations = await fetchOrganizations();
    setProjectModalData({
      title: data?.title || '',
      subTitle: data?.subTitle || '',
      organizationId: data?.org !== undefined
        ? String(data.org)
        : (loadedOrganizations[0] ? String(loadedOrganizations[0].id) : ''),
      file: null
    });
    setShowProjectModal(true);
  }

  function closeProjectModal() {
    setShowProjectModal(false);
  }

  async function handleProjectSubmit(event) {
    event.preventDefault();

    if (!projectModalData?.title.trim()) {
      alert('Title is required');
      return;
    }

    const organizationId = isSiteAdmin
      ? projectModalData.organizationId
      : (data?.org !== undefined ? String(data.org) : '');
    if (!organizationId) {
      alert('Organization is required');
      return;
    }

    setProjectSaving(true);
    try {
      const response = await submitProjectUpdate({
        title: projectModalData.title,
        subTitle: projectModalData.subTitle,
        description: data?.description || '',
        organizationId,
        file: projectModalData.file
      });

      if (response?.ok === true) {
        const nextProject = response?.project || {
          ...data,
          title: projectModalData.title,
          subTitle: projectModalData.subTitle,
          org: Number(organizationId) || data?.org
        };
        const nextSlugURL = nextProject?.slugURL || data?.slugURL || '';
        setData((current) => current ? { ...current, ...nextProject } : current);
        closeProjectModal();
        if (nextSlugURL && nextSlugURL !== project) {
          navigate({
            pathname: `${config.prefixPath}/projects/${nextSlugURL}`,
            search: location.search
          });
        }
      } else if (response?.error) {
        alert(response.message || 'Failed to update project.');
      }
    } catch (e) {
      alert(e?.message || 'Failed to update project.');
    } finally {
      setProjectSaving(false);
    }
  }

  const handleProjectDescriptionChange = useCallback(async (_path, nextValue) => {
    if (!canEditProject || !data?.slugURL) {
      return;
    }

    const previousDescription = (data?.description || '').toString();
    setData((current) => current ? { ...current, description: nextValue } : current);

    try {
      const response = await submitProjectUpdate({
        title: data?.title || '',
        subTitle: data?.subTitle || '',
        description: nextValue,
        organizationId: data?.org
      });

      if (response?.ok === true) {
        const nextProject = response?.project || { ...data, description: nextValue };
        setData((current) => current ? { ...current, ...nextProject } : current);
        return;
      }

      throw new Error(response?.message || 'Failed to update project description.');
    } catch (e) {
      setData((current) => current ? { ...current, description: previousDescription } : current);
      alert(e?.message || 'Failed to update project description.');
    }
  }, [canEditProject, data, submitProjectUpdate]);

  async function handleDeleteProject() {
    if (!data?.slugURL || !canDeleteProject) {
      return;
    }
    const shouldDelete = window.confirm('Delete this project? This action cannot be undone.');
    if (!shouldDelete) return;

    setProjectDeleting(true);
    try {
      const response = await api.delete(`/api/v1/projects/${data.slugURL}`);
      if (response?.ok === true) {
        navigate(`${config.prefixPath}/projects`);
        return;
      }
      if (response?.error) {
        alert(response.message || 'Failed to delete project.');
      }
    } catch (e) {
      alert(e?.message || 'Failed to delete project.');
    } finally {
      setProjectDeleting(false);
    }
  }

  function resolveItemReference(reference) {
    const bomSlugKey = normalizeBomReferenceKey(reference?.bomSlug);
    const identifierKey = normalizeBomReferenceKey(reference?.identifier);
    const bom = bomReferenceLookup.get(bomSlugKey);
    if (!bom) {
      return {
        found: false,
        bom: null,
        item: null
      };
    }

    const item = bom.itemMap.get(identifierKey) || null;
    if (!item) {
      return {
        found: false,
        bom,
        item: null
      };
    }

    return {
      found: true,
      bom,
      item
    };
  }

  function resolveBomTagReference(reference) {
    const bomSlugKey = normalizeBomReferenceKey(reference?.slug);
    const bom = bomReferenceLookup.get(bomSlugKey);
    return {
      found: !!bom,
      bom: bom || null
    };
  }

  function resolveGuideTagReference(reference) {
    const guideSlugKey = normalizeBomReferenceKey(reference?.slug);
    const guideEntry = guideReferenceLookup.get(guideSlugKey);
    return {
      found: !!guideEntry,
      guide: guideEntry || null
    };
  }

  function getItemReferenceDisplayName(reference, resolved) {
    const optionalName = (reference?.optionalName || '').toString().trim();
    if (optionalName) {
      return optionalName;
    }
    if (resolved?.found && resolved?.item) {
      return (resolved.item.name || resolved.item.title || reference?.identifier || 'unknown item').toString();
    }
    return (reference?.identifier || 'unknown item').toString();
  }

  function getEntityReferenceDisplayName(reference, resolved) {
    const optionalName = (reference?.optionalName || '').toString().trim();
    if (optionalName) return optionalName;

    if (reference?.type === 'bom') {
      if (resolved?.found && resolved?.bom) {
        return (resolved.bom.title || resolved.bom.slugURL || reference?.slug || 'unknown BOM').toString();
      }
      return (reference?.slug || 'unknown BOM').toString();
    }

    if (reference?.type === 'guide') {
      if (resolved?.found && resolved?.guide) {
        return (resolved.guide.title || resolved.guide.slugURL || reference?.slug || 'unknown guide').toString();
      }
      return (reference?.slug || 'unknown guide').toString();
    }

    return 'unknown reference';
  }

  function buildBomItemReferenceUrl(resolved) {
    if (!resolved?.found || !resolved?.bom || !resolved?.item || !data?.slugURL) return '';
    const focusToken = resolved.item.stepId || resolved.item.identifier;
    const basePath = `${config.prefixPath}/projects/${data.slugURL}/guides/${resolved.bom.slugURL}`;
    if (!focusToken) {
      return basePath;
    }
    const anchorToken = resolved.item.stepId || `item-${resolved.item.position || ''}`;
    return `${basePath}?${BOM_URL_ITEM_PARAM}=${encodeURIComponent(focusToken)}#bom-item-${anchorToken}`;
  }

  function buildEntityReferenceUrl(reference, resolved) {
    if (!data?.slugURL) return '';
    if (reference?.type === 'bom') {
      const slugURL = resolved?.bom?.slugURL || '';
      if (!slugURL) return '';
      const params = new URLSearchParams();
      const filterType = normalizeBomFilterType(reference?.filterType);
      const filterValue = (reference?.filterValue || '').toString().trim();
      if (filterType && filterValue) {
        if (filterType === 'version') {
          params.set(BOM_URL_VERSION_PARAM, filterValue);
        } else {
          params.set(BOM_URL_VIEW_PARAM, filterType);
          params.set(BOM_URL_FOCUS_PARAM, filterValue);
        }
      }
      const query = params.toString();
      return `${config.prefixPath}/projects/${data.slugURL}/guides/${slugURL}${query ? `?${query}` : ''}`;
    }
    if (reference?.type === 'guide') {
      const slugURL = resolved?.guide?.slugURL || '';
      if (!slugURL) return '';
      return `${config.prefixPath}/projects/${data.slugURL}/guides/${slugURL}`;
    }
    return '';
  }

  function ProjectReferenceInline({ reference }) {
    const resolved = reference?.type === 'item'
      ? resolveItemReference(reference)
      : (reference?.type === 'bom' ? resolveBomTagReference(reference) : resolveGuideTagReference(reference));
    const displayName = reference?.type === 'item'
      ? getItemReferenceDisplayName(reference, resolved)
      : getEntityReferenceDisplayName(reference, resolved);
    const href = reference?.type === 'item'
      ? buildBomItemReferenceUrl(resolved)
      : buildEntityReferenceUrl(reference, resolved);

    if (!href) {
      return <span className="bom-reference-missing">{displayName}</span>;
    }
    return <a href={href}>{displayName}</a>;
  }

  function isExternalMarkdownLink(href) {
    const rawHref = (href || '').toString().trim();
    if (!rawHref || rawHref.startsWith('#')) return false;
    if (!/^(https?:)?\/\//i.test(rawHref)) return false;

    try {
      const browserOrigin = typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : '';
      const parsed = new URL(rawHref, browserOrigin || 'https://stepwisedocs.local');
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
      if (!browserOrigin) return true;
      return parsed.origin !== browserOrigin;
    } catch (e) {
      return false;
    }
  }

  function MarkdownLink({ href, children, ...props }) {
    const markdownReference = parseMarkdownReferenceHref(href);
    if (markdownReference && (markdownReference.type === 'item' || markdownReference.type === 'bom' || markdownReference.type === 'guide')) {
      return <ProjectReferenceInline reference={markdownReference} />;
    }

    const isExternal = isExternalMarkdownLink(href);
    return (
      <a
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {children}
      </a>
    );
  }

  const projectDescriptionMarkdownComponents = {
    a: MarkdownLink,
    li: CustomListItem,
    ul: ({ node, ...props }) => (
      <ListTypeContext.Provider value="ul">
        <ul className="step-items" {...props} />
      </ListTypeContext.Provider>
    ),
    ol: ({ node, ...props }) => (
      <ListTypeContext.Provider value="ol">
        <ol className="step-itemso" {...props} />
      </ListTypeContext.Provider>
    )
  };

  const availableTags = Array.isArray(data?.allTags) ? normalizeTagList(data.allTags) : normalizeGuideTags(data?.guides || []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!showProjectModal) {
      setProjectModalData({
        title: '',
        subTitle: '',
        organizationId: '',
        file: null
      });
    }
  }, [showProjectModal]);

  if (error === '404') return (<Page404/>);
  if (!data) {
    return (
      <>
      <HeaderNavbar/>
      <div className="project-page">
        <div className="project-loading-state">
          <Spinner animation="border" />
        </div>
      </div>
      <Footer/>
      </>
    );
  }

  function isTagSelected(tag) {
    const normalizedTag = (tag || '').toString().trim().toLowerCase();
    if (!normalizedTag) return false;
    return selectedTags.some((selectedTag) => selectedTag.toLowerCase() === normalizedTag);
  }

  function toggleTagFilter(tag) {
    const normalizedTag = (tag || '').toString().trim();
    if (!normalizedTag) return;

    const normalizedTagKey = normalizedTag.toLowerCase();
    const nextTags = isTagSelected(normalizedTag)
      ? selectedTags.filter((selectedTag) => selectedTag.toLowerCase() !== normalizedTagKey)
      : [...selectedTags, normalizedTag];
    const params = new URLSearchParams(location.search);
    params.delete('filterByTag');
    nextTags.forEach((selectedTag) => params.append('filterByTag', selectedTag));
    const search = params.toString();
    navigate({
      pathname: location.pathname,
      search: search ? `?${search}` : ''
    });
  }

  function clearTagFilters() {
    if (!hasTagFilters) return;
    const params = new URLSearchParams(location.search);
    params.delete('filterByTag');
    const search = params.toString();
    navigate({
      pathname: location.pathname,
      search: search ? `?${search}` : ''
    });
  }

  function drawTags() {
    if (!availableTags.length) return null;
    return (
      <div className="project-filter-panel" aria-label="Guide tag filters">
        <div className="project-filter-header">
          <span className="project-filter-title">Filter by tags</span>
          {hasTagFilters ? (
            <button
              type="button"
              className="project-filter-clear"
              onClick={clearTagFilters}
            >
              Clear filters
            </button>
          ) : null}
        </div>
        <div className="project-filter-tags">
          {availableTags.map((tag) => (
            <button
              type="button"
              key={tag}
              className={`project-filter-tag ${isTagSelected(tag) ? 'is-active' : ''}`}
              onClick={() => toggleTagFilter(tag)}
              aria-pressed={isTagSelected(tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>
    );
  }

  function drawGuideTags(guide) {
    const guideTags = (Array.isArray(guide?.tags) ? guide.tags : []).filter((tag) => tag && tag.toString().trim());
    if (!guideTags.length) return null;
    return (
      <div className="project-guide-tags">
        {guideTags.map((tag, index) => (
          <span
            key={`${guide.id}-${tag}-${index}`}
            className="project-guide-tag"
          >
            {tag}
          </span>
        ))}
      </div>
    );
  }

  async function saveOrder(newOrder) {
    if (!canEditProject) return;
    try {
      let response = await api.put(`/api/v1/projects/${project}/guides`, {guides: newOrder} );
      if (response?.ok === true) {

      }
    } catch (e) {
      if (e.statusCode === 404) {
        return setError('404')
      }
    }
  }

  function onSortEnd (oldIndex, newIndex) {
    if (!canEditProject) return;
    let newGuides = arrayMoveImmutable([...data.guides], oldIndex, newIndex);
    let newData = {
      ...data,
      guides: [...newGuides],
    }

    let newOrder = newData.guides.map((g, i) => {
      return {id: g.id, order: ++i};
    })
    setData(newData);
    saveOrder(newOrder);
  }

  function drawProgress (guide) {
    if ((guide?.type || '').toString().toLowerCase() === 'bom') return null;
    const progress = guide?.progress;
    if (!progress || progress.tracking === false || !Array.isArray(progress.steps)) return null;

    const completedCount = progress.steps.length;
    const totalSteps = Array.isArray(guide?.steps) ? guide.steps.length : 0;
    if (!totalSteps) return null;

    if (completedCount >= totalSteps) {
      return <span className="project-pill project-pill-success">Completed</span>;
    }

    return (
      <span className="project-pill project-pill-warning">
        In progress {completedCount}/{totalSteps}
      </span>
    );
  }

  function getGuideProgressStatus(guide) {
    if ((guide?.type || '').toString().toLowerCase() === 'bom') return 'excluded';
    const totalSteps = Array.isArray(guide?.steps) ? guide.steps.length : 0;
    if (!totalSteps) return 'not-started';
    const progress = guide?.progress;
    if (!progress || progress.tracking === false || !Array.isArray(progress.steps)) {
      return 'not-started';
    }
    const completedCount = progress.steps.length;
    if (completedCount >= totalSteps) return 'completed';
    return 'in-progress';
  }

  function drawProjectProgressBar() {
    const guides = (Array.isArray(data?.guides) ? data.guides : [])
      .filter((guide) => (guide?.type || '').toString().toLowerCase() !== 'bom');
    if (!guides.length) return null;

    const segments = guides.map((guide) => ({
      guide,
      status: getGuideProgressStatus(guide)
    }));

    const hasTracked = segments.some(segment => segment.status !== 'not-started');
    if (!hasTracked) return null;

    const counts = segments.reduce(
      (acc, segment) => {
        if (segment.status === 'completed') acc.completed += 1;
        else if (segment.status === 'in-progress') acc.inProgress += 1;
        else acc.notStarted += 1;
        return acc;
      },
      { completed: 0, inProgress: 0, notStarted: 0 }
    );

    const progressLabel = `Project guide progress: ${counts.completed} completed, ${counts.inProgress} in progress, ${counts.notStarted} not started.`;

    const statusLabel = (status) => {
      if (status === 'completed') return 'Completed';
      if (status === 'in-progress') return 'In progress';
      return 'Not started';
    };

    return (
      <div className="project-progress">
        <div className="project-progress-header">
          <div className="project-progress-label">Your project progress</div>
          <div className="project-progress-legend" aria-hidden="true">
            <span className="project-progress-legend-item">
              <span className="project-progress-legend-dot project-progress-completed" />
              Completed
            </span>
            <span className="project-progress-legend-item">
              <span className="project-progress-legend-dot project-progress-in-progress" />
              In progress
            </span>
            <span className="project-progress-legend-item">
              <span className="project-progress-legend-dot project-progress-not-started" />
              Not started
            </span>
          </div>
        </div>
        <div className="project-progress-bar" role="img" aria-label={progressLabel}>
          {segments.map((segment, index) => (
            <div
              key={`${segment.guide?.id || segment.guide?.slugURL || index}-${segment.status}`}
              className={`project-progress-segment project-progress-${segment.status}`}
              title={`${segment.guide?.title || 'Guide'}: ${statusLabel(segment.status)}`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
    <HeaderNavbar/>  
    <div className="project-page">
      <ol className="breadcrumb navi">
        <li className="breadcrumb-item"><a href={`${config.prefixPath}/projects`}>Projects</a></li>
        <li className="breadcrumb-item active" aria-current="page">{data.title}</li>
      </ol>

      <section className="project-hero-card">
        <LazyImage
          src={data.image || "https://placehold.co/2500x280"}
          className="project-hero-image"
          style={{
            width: '100%',
            height: 'var(--project-hero-image-height)',
            maxHeight: 'var(--project-hero-image-height)',
            objectFit: 'cover',
            objectPosition: 'center'
          }}
          alt={data.title || "Project image"}
        />
        <div className="project-hero-body">
          <div className="project-hero-title-row">
            <div className="project-title-main">
              <h1 className="project-title">{data.title}</h1>
              {showProjectSettingsMenu ? (
                <Dropdown align="end" className="project-settings-menu">
                  <Dropdown.Toggle
                    variant="link"
                    id="project-settings-toggle"
                    className="project-settings-toggle"
                    aria-label="Project settings"
                    disabled={projectDeleting}
                  >
                    {projectDeleting ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faGear} />}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {canEditProject ? (
                      <Dropdown.Item as="button" type="button" onClick={openProjectModal}>
                        <FontAwesomeIcon icon={faPenToSquare} className="me-2" />
                        Edit project
                      </Dropdown.Item>
                    ) : null}
                    {canDeleteProject ? (
                      <Dropdown.Item as="button" type="button" onClick={handleDeleteProject} className="text-danger">
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        Delete project
                      </Dropdown.Item>
                    ) : null}
                  </Dropdown.Menu>
                </Dropdown>
              ) : null}
            </div>
            {isPending ? (
              <span className="project-pill project-pill-warning">Pending approval</span>
            ) : null}
          </div>
          {data.subTitle ? <p className="project-subtitle">{data.subTitle}</p> : null}
          {(canEditProject || data.description) ? (
            <div className="project-description">
              <EditableTextArea
                editable={canEditProject}
                name="projectDescription"
                index={0}
                value={data.description || ''}
                onValueChange={handleProjectDescriptionChange}
                path="project.description"
                contentStyle={{
                  border: '1px solid #d8e0ea',
                  borderRadius: 12,
                  marginTop: 10,
                  padding: '0.9rem 1rem',
                  background: canEditProject ? '#fbfdff' : '#fff',
                  minHeight: 72
                }}
                editStyle={{
                  width: '100%',
                  minHeight: 240,
                  height: 240,
                  marginTop: 10
                }}
                markdownComponents={projectDescriptionMarkdownComponents}
                markdownRemarkPlugins={markdownRemarkPlugins}
                bomAutocompleteCatalog={bomReferenceCatalog}
                guideAutocompleteCatalog={guideReferenceCatalog}
              />
            </div>
          ) : null}
        </div>
      </section>

      <section className="project-guides-section">
        <div className="project-guides-header">
          <div>
            <h2 className="project-guides-title">Guides & BOMs</h2>
            <p className="project-guides-subtitle">
              {(data?.guides || []).length} item{(data?.guides || []).length === 1 ? '' : 's'}
              {hasTagFilters ? ` matching ${selectedTags.length} active filter${selectedTags.length === 1 ? '' : 's'}` : ''}
            </p>
          </div>
          {canEditProject ? (
            <div className="btn-group">
              <button className="btn btn-sm btn-outline-dark" onClick={() => handleShowCreateModal('new-guide')}>
                Create Guide
              </button>
              <button className="btn btn-sm btn-outline-dark" onClick={() => handleShowCreateModal('new-bom')}>
                Create BOM
              </button>
            </div>
          ) : null}
        </div>

        {drawProjectProgressBar()}
        {drawTags()}

        <SortableList
          disabled={!canEditProject || hasTagFilters}
          onSortEnd={(oldIndex, newIndex) => onSortEnd(oldIndex, newIndex)}
          draggedItemClassName="project-guide-card-dragging"
          style={{display: 'block', userSelect: 'none'}}
        >
          <div className="row row-cols-1 row-cols-sm-2 row-cols-md-2 row-cols-xl-3 g-4 project-guides-grid">
            {(!data.guides || data.guides.length === 0) && (
              <div className="col-12">
                <div className="project-empty-guides">No guides or BOMs found for the current filters.</div>
              </div>
            )}

            {(data?.guides || []).map((guide) => {
              const descriptionPreview = sanitizeMarkdownPreviewText(guide.description);
              return (
              <div className="col" key={guide.id || guide.slugURL}>
                <SortableItem key={`sort-${guide.id || guide.slugURL}`}>
                  <article className="project-guide-card">
                    <div className="project-guide-image-wrapper">
                      <a href={`${config.prefixPath}/projects/${project}/guides/${guide.slugURL}`} className="project-guide-image-link">
                        <LazyImage
                          src={guide?.mainImage?.url || 'https://placehold.co/286x180' }
                          className="project-guide-image"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center'
                          }}
                          alt={guide.title || "Guide image"}
                        />
                      </a>
                      {(guide?.type || '').toString().toLowerCase() === 'bom' ? (
                        <div className="project-guide-ribbon">BOM</div>
                      ) : null}
                      {(canEditProject && !hasTagFilters) ? (
                        <SortableKnob>
                          <div title="Grab to sort" className="project-sort-grab">
                            <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                          </div>
                        </SortableKnob>
                      ) : null}
                    </div>
                    <div className="project-guide-body">
                      <a href={`${config.prefixPath}/projects/${project}/guides/${guide.slugURL}`} className="project-guide-title-link">
                        <h3 className="project-guide-title">{guide.title}</h3>
                      </a>
                      <div className="project-guide-pills">
                        {(guide?.type || '').toString().toLowerCase() === 'bom' ? (
                          <span className="project-pill project-pill-bom">BOM</span>
                        ) : null}
                        {guide.published ? null : (
                          <span className="project-pill project-pill-danger">Draft</span>
                        )}
                        {canManageProject && (guide?.pendingCommentsCount || 0) > 0 ? (
                          <span className="project-pill project-pill-warning">
                            {guide?.pendingCommentsCount || 0} pending comment{(guide?.pendingCommentsCount || 0) === 1 ? '' : 's'}
                          </span>
                        ) : null}
                        {canManageProject && (guide?.pendingTodoCount || 0) > 0 ? (
                          <span className="project-pill project-pill-warning">
                            {guide?.pendingTodoCount || 0} TODO{(guide?.pendingTodoCount || 0) === 1 ? '' : 's'} pending
                          </span>
                        ) : null}
                        {drawProgress(guide)}
                      </div>
                      {descriptionPreview ? (
                        <p className="project-guide-description">{descriptionPreview}</p>
                      ) : (
                        <p className="project-guide-description project-guide-description-empty">No description provided.</p>
                      )}
                      {drawGuideTags(guide)}
                      <small className="project-guide-updated">
                        Last updated {moment(guide.updateDate).fromNow()}.
                      </small>
                    </div>
                  </article>
                </SortableItem>
              </div>
              );
            })}
          </div>
        </SortableList>
      </section>
    </div>
    <div className={`modal ${showCreateModal ? 'show' : ''}`} tabIndex="-1" style={showCreateModal ? { display: 'block' } : { display: 'none' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{createModalType === 'new-bom' ? 'New BOM' : 'New Guide'}</h5>
            </div>
            <div className="modal-body">
              <form>
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" className="form-control" name="title" value={createModalData.title} onChange={handleCreateInputChange} />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea className="form-control" name="description" value={createModalData.description} onChange={handleCreateInputChange} />
                </div>
              </form>
            </div>
            <div className="modal-footer">
              {createLoading ? <Spinner animation="border" /> :
              <>
                <button type="button" className="btn btn-sm btn-outline-dark" onClick={handleCloseCreateModal}>Close</button>
                <button type="button" className="btn btn-sm btn-outline-dark" onClick={handleCreateSubmit}>Save changes</button>
              </>}
            </div>
          </div>
        </div>
      </div>
      <div className={`modal ${showProjectModal ? 'show' : ''}`} tabIndex="-1" style={showProjectModal ? { display: 'block' } : { display: 'none' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit Project</h5>
            </div>
            <div className="modal-body">
              <form id="project-edit-form">
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" className="form-control" name="title" value={projectModalData.title} onChange={handleProjectInputChange} />
                </div>
                <div className="form-group">
                  <label>Subtitle</label>
                  <input type="text" className="form-control" name="subTitle" value={projectModalData.subTitle} onChange={handleProjectInputChange} />
                </div>
                <div className="form-group">
                  <label>Organization</label>
                  <select
                    className="form-control"
                    name="organizationId"
                    value={projectModalData.organizationId}
                    onChange={handleProjectInputChange}
                    disabled={loadingOrganizations || !isSiteAdmin}
                  >
                    {organizationOptions.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                  {!isSiteAdmin ? (
                    <small className="form-text text-muted">Only site admins can move a project to another organization.</small>
                  ) : null}
                </div>
                <div className="form-group">
                  <label>Image</label>
                  <input onChange={handleProjectFileChange} className="form-control" name="images" type="file" accept="image/*;capture=camera" multiple={false} />
                </div>
              </form>
            </div>
            <div className="modal-footer">
              {projectSaving ? (
                <Spinner animation="border" />
              ) : (
                <>
                  <button type="button" className="btn btn-sm btn-outline-dark" onClick={closeProjectModal}>Close</button>
                  <button type="button" className="btn btn-sm btn-outline-dark" onClick={handleProjectSubmit}>Save changes</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer/> 
    </>
  );
}

export default ProjectHome;
